import styles from './PositionsTable.module.scss';

import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import InsertPageBreakIcon from '@mui/icons-material/InsertPageBreak';
import { IconButton } from '@mui/material';
import clsx from 'clsx';
import React, { DetailedHTMLProps, HTMLAttributes, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DragIndicatorIcon } from '@work4all/assets/icons/drag-vertical.svg';

import { useUser } from '@work4all/data';

import { Position } from '@work4all/models/lib/Classes/Position.entity';
import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';

import { formatCurrency, formatNumberAsCurrency } from '@work4all/utils';
import { canViewERPPrices } from '@work4all/utils/lib/permissions';

import { useFormContextPlus } from '../../../../../../../../form-plus/use-form-context-plus';
import { ErpData } from '../../../../../ErpData';
import { useEditTable } from '../edit-table/components/edit-table-provider/EditTableProvider';
import { useEditTableDrag } from '../edit-table/components/TableRow/use-edit-table-drag';
import { useEditTableAllCells } from '../edit-table/hooks/use-edit-table-all-cells';
import { useEditableRow } from '../edit-table/hooks/use-editable-row';
import { STUECKLISTE_GROUP } from '../edit-table/hooks/use-editable-state';
import { InfiniteRow } from '../edit-table/InfiniteList';
import { EditTableProps } from '../edit-table/types';
import { useSelectedPositionState } from '../hooks/use-selected-position';

interface PositionsMobileTableProps {
  position: Position;
  setEditedPosition: (position: Position) => void;
  allPositions: Position[];
  editableRows?: EditTableProps<Position>['editableRows'];
  columns?: EditTableProps<Position>['columns'];
  decorators?: EditTableProps<Position>['decorators'];
}

const wrapWithRow = (position: Position) => {
  return {
    id: position.localId,
    index: position.index,
    original: position,
  } as unknown as InfiniteRow<Position>;
};

export const PositionsMobileTable = (props: PositionsMobileTableProps) => {
  const {
    setEditedPosition,
    allPositions,
    position,
    columns,
    editableRows,
    decorators,
  } = props;

  const { onCollapsePosition, onMovePosition } = useEditTable();
  const allCells = useEditTableAllCells<Position>(columns);

  const data = useRef(allPositions);
  data.current = allPositions;

  const { calculateRowProperties } = useEditableRow({
    allCells,
    data,
    decorators,
    editableRows,
    onMovePosition,
  });

  const { canDrop, handleMoveRow } = calculateRowProperties(
    wrapWithRow(position),
    allPositions.map(wrapWithRow)
  );

  const { direction, isDragging, isOver, dropRef, dragRef } = useEditTableDrag<
    HTMLTableRowElement,
    HTMLTableCellElement
  >({
    canDrop,
    index: allPositions.findIndex((x) => x.localId === position.localId),
    localId: position.localId,
    moveRowDone: handleMoveRow,
  });

  const user = useUser();
  const showPrices = canViewERPPrices(user);
  const { watch } = useFormContextPlus<ErpData>();
  const currency = watch('currency');
  const { t } = useTranslation();
  const { setSelectedPositionIds } = useSelectedPositionState();

  const key = position.id;

  const bomComponentList = position.posId
    ? allPositions.filter((x) => x.posId === position.posId)
    : null;
  const isLastSubPosition =
    bomComponentList &&
    bomComponentList.findIndex((x) => x.id === position.id) ===
      bomComponentList.length - 1;

  const dragStyle: React.CSSProperties = {
    opacity: isDragging ? 0.2 : 1,
    userSelect: 'none',
    WebkitUserSelect: 'none',
  };
  const rowProps: DetailedHTMLProps<
    HTMLAttributes<HTMLTableRowElement>,
    HTMLTableRowElement
  > = {
    style: dragStyle,
    className: isDragging ? styles.noselect : undefined,
    onContextMenu: (e) => e.preventDefault(),
  };

  const dragCell = (
    <td ref={dragRef}>
      <DragIndicatorIcon />
    </td>
  );

  const getRow = () => {
    switch (position.positionKind) {
      case ErpPositionsKind.SEITENUMBRUCH:
        return (
          <tr ref={dropRef} key={key} {...rowProps}>
            {dragCell}
            <td
              colSpan={3}
              className={clsx(styles['tr-border'], styles.mobilePositionRow)}
            >
              <div
                className={styles['page-break']}
                onClick={() => {
                  setEditedPosition(position);
                  setSelectedPositionIds([position.id]);
                }}
                style={{ width: 'fit-content' }}
              >
                <InsertPageBreakIcon />
                <div>{t('COMMON.PAGE_BREAK')}</div>
              </div>
            </td>
          </tr>
        );
      case ErpPositionsKind.TITEL:
      case ErpPositionsKind.TITELSUMME:
      case ErpPositionsKind.ZWISCHENSUMME:
        return (
          <tr
            ref={dropRef}
            style={dragStyle}
            key={key}
            onClick={() => {
              setEditedPosition(position);
              setSelectedPositionIds([position.id]);
            }}
            className={clsx(
              styles.title,
              styles.mobilePositionRow,
              rowProps.className,
              {
                [styles.text01]:
                  position.positionKind === ErpPositionsKind.TITEL,
              }
            )}
          >
            {dragCell}
            <td></td>
            <td>{position.number}</td>
            <td style={{ width: '100%' }}>
              {position.positionKind !== ErpPositionsKind.TITEL &&
                showPrices && (
                  <span style={{ float: 'right' }}>
                    {formatNumberAsCurrency(position.totalPriceNet)}{' '}
                    {formatCurrency(currency, t)}
                  </span>
                )}
              <div style={{ whiteSpace: 'pre-line' }}>{position.longtext}</div>
            </td>
          </tr>
        );
      default:
        return (
          <tr
            ref={dropRef}
            key={key}
            className={clsx(styles.mobilePositionRow, rowProps.className)}
            style={dragStyle}
          >
            {dragCell}
            <td>
              {STUECKLISTE_GROUP.includes(position.positionKind) && (
                <IconButton
                  size="small"
                  onClick={() => onCollapsePosition(position)}
                  style={{
                    marginTop: '-0.25rem',
                  }}
                >
                  {allPositions.find((x) => x.posId === position.id) ? (
                    <KeyboardArrowDown />
                  ) : (
                    <KeyboardArrowRight />
                  )}
                </IconButton>
              )}
            </td>
            <td
              className={styles.positionNumberCell}
              onClick={() => {
                setEditedPosition(position);
                setSelectedPositionIds([position.id]);
              }}
            >
              {position.number}
              {position.posId ? (
                <>
                  <div
                    className={clsx(styles.subPositionLineVert, {
                      [styles.last]: isLastSubPosition,
                    })}
                  ></div>
                  <div className={styles.subPositionLineHor}></div>
                </>
              ) : null}
            </td>
            <td
              style={{ width: '100%' }}
              onClick={() => {
                setEditedPosition(position);
                setSelectedPositionIds([position.id]);
              }}
            >
              <div style={{ whiteSpace: 'pre-line' }}>{position.longtext}</div>

              <div className={styles.subline}>
                <div className={styles.left}>
                  <span>
                    {position.amount} {position.unit}
                  </span>
                  {showPrices && (
                    <span
                      className={clsx({
                        [styles.strikethrough]: position.insteadOfTotalPrice,
                      })}
                    >
                      {formatNumberAsCurrency(position.singlePriceNet)}{' '}
                      {formatCurrency(currency, t)}
                    </span>
                  )}
                  {showPrices && position.discount > 0 && (
                    <span>{position.discount} %</span>
                  )}
                </div>
                {showPrices && (
                  <div className={styles.right}>
                    {position.insteadOfTotalPrice ? (
                      position.insteadOfTotalPrice
                    ) : (
                      <>
                        {formatNumberAsCurrency(position.totalPriceNet)}{' '}
                        {formatCurrency(currency, t)}
                      </>
                    )}
                  </div>
                )}
              </div>
            </td>
          </tr>
        );
    }
  };

  return (
    <React.Fragment key={key}>
      {isOver && direction === 'up' ? (
        <tr className={styles['highlight-row']}>
          <td colSpan={4} />
        </tr>
      ) : undefined}
      {getRow()}
      {isOver && direction === 'down' ? (
        <tr className={styles['highlight-row']}>
          <td colSpan={4} />
        </tr>
      ) : undefined}
    </React.Fragment>
  );
};
